import { Injectable, computed, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { LIMIT_LABELS, FEATURE_LABELS } from '../utils/entitlement-labels';

export interface PlanLimitModalState {
  kind: 'limit' | 'feature';
  key: string;
  label: string;
  current?: number | null;
  max?: number | null;
  planCode?: string | null;
  message?: string | null;
}

/**
 * Shows a blocking "upgrade required" modal when a create/action is rejected by the
 * backend's RequireWithinLimit/RequireFeature gates (LIMIT_REACHED / FEATURE_NOT_INCLUDED),
 * or when a client-side pre-check (EntitlementService) catches the same condition before
 * the request is even sent.
 */
@Injectable({ providedIn: 'root' })
export class PlanLimitModalService {
  private auth = inject(AuthService);

  private _state = signal<PlanLimitModalState | null>(null);
  readonly state = this._state.asReadonly();

  // /billing/change-plan is SuperAdmin-only, so only SuperAdmin gets the upgrade CTA.
  readonly canManageBilling = computed(() => this.auth.isSuperAdmin());

  showLimit(p: { limit: string; current?: number | null; max?: number | null; planCode?: string | null; message?: string | null }): void {
    this._state.set({
      kind: 'limit',
      key: p.limit,
      label: LIMIT_LABELS[p.limit] ?? p.limit,
      current: p.current,
      max: p.max,
      planCode: p.planCode,
      message: p.message,
    });
  }

  showFeature(p: { feature: string; planCode?: string | null; message?: string | null }): void {
    this._state.set({
      kind: 'feature',
      key: p.feature,
      label: FEATURE_LABELS[p.feature] ?? p.feature,
      planCode: p.planCode,
      message: p.message,
    });
  }

  dismiss(): void {
    this._state.set(null);
  }
}
